import { useSuspenseQuery } from "@tanstack/react-query";
import { normalizeDate } from "../utils/date";
import type { MetricName } from "../types";
import { METRIC_STALE_TIME } from "../constants";
import { DEFAULT_CAMPAIGN } from "../constants/campaignPerformance";
import { fetchCampaignPerformance, type CampaignPerformanceRequest } from "./useCampaignPerformanceQuery";

const ALL_AIRLINES_ENDPOINT = "/api/v1/campaigns/performance/airlines";

const OVERVIEW_METRICS = [
  "DISCOUNTED_BOOKINGS_SOLD",
  "AVERAGE_DISCOUNT_PER_BOOKING",
  "AVERAGE_DISCOUNT_PERCENTAGE_PER_BOOKING",
] as const satisfies readonly MetricName[];

const PER_AIRLINE_METRICS = [...OVERVIEW_METRICS, "SEARCH_COVERAGE_PERCENTAGE"] as const satisfies readonly MetricName[];

export type AirlinesCampaignPerformanceQueryParams = {
  from: Date;
  to: Date;
  metrics: MetricName[];
  airlineCode?: string;
};

const getEndpoint = (airlineCode?: string) =>
  airlineCode ? `${ALL_AIRLINES_ENDPOINT}/${airlineCode}` : ALL_AIRLINES_ENDPOINT;

export const useAirlinesCampaignPerformanceQuery = ({
  from,
  to,
  metrics,
  airlineCode,
}: AirlinesCampaignPerformanceQueryParams) => {
  const fromDate = normalizeDate(from);
  const toDate = normalizeDate(to);
  const endpoint = getEndpoint(airlineCode);
  const requestMetrics = airlineCode ? PER_AIRLINE_METRICS : OVERVIEW_METRICS;

  const request: CampaignPerformanceRequest = {
    from: fromDate,
    to: toDate,
    campaign: DEFAULT_CAMPAIGN,
    metrics: [...requestMetrics],
  };

  return useSuspenseQuery({
    queryKey: [endpoint, fromDate.toISOString(), toDate.toISOString(), DEFAULT_CAMPAIGN, requestMetrics],
    queryFn: () => fetchCampaignPerformance(endpoint, request),
    select: (data) => data.metrics.filter((metric) => metrics.includes(metric.metadata.metricName)),
    staleTime: METRIC_STALE_TIME,
  });
};
